import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "component-design",
  name: "コンポーネント設計",
  nameEn: "Component Design",
  seo: {
    title:
      "コンポーネント設計を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Atomic Design、再利用可能なUI設計、Storybook、デザインシステム構築を実践的に学べるゆめさくエンジニア。フロントエンドリードへのキャリアを目指しましょう。",
    url: "/columns/component-design-programming-school",
  },
  hero: {
    title: "コンポーネント設計を学べる女性向けプログラミングスクール",
    englishTitle: "Learn Component Design",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "コンポーネント設計" },
  ],
  overview: {
    heading: "コンポーネント設計とは？",
    paragraphs: [
      "コンポーネント設計とは、UIを再利用可能な部品（コンポーネント）に分割し、保守性・拡張性の高いフロントエンドアプリケーションを構築するための設計手法です。Atomic Design、Compound Components、Headless UIなど、さまざまな設計パターンがあります。",
      "Storybookを使ったコンポーネントカタログの構築、デザインシステムの設計・運用、アクセシビリティ対応など、チーム開発で品質の高いUIを効率的に開発するためのスキルセットが求められます。",
      "ReactやTypeScriptを使った開発において、コンポーネントの責務分離、Propsの設計、合成パターン（Composition）を適切に行えるエンジニアは、フロントエンドチームで非常に重宝されます。",
    ],
    useCases: [
      {
        label: "デザインシステムの構築",
        description:
          "ボタン、フォーム、モーダルなどの共通UIコンポーネントを体系化し、組織全体で一貫したデザインを実現するデザインシステムを構築します。",
      },
      {
        label: "Storybookによるコンポーネントカタログ",
        description:
          "各コンポーネントのバリエーションやインタラクションをStorybookで可視化し、開発者・デザイナー間のコミュニケーションを円滑にします。",
      },
      {
        label: "アクセシブルなUI実装",
        description:
          "WAI-ARIAに準拠したキーボード操作、スクリーンリーダー対応など、すべてのユーザーが使いやすいUI コンポーネントを設計・実装します。",
      },
      {
        label: "大規模フロントエンドの設計",
        description:
          "数百画面規模のアプリケーションでも破綻しない、スケーラブルなコンポーネント階層とディレクトリ構造を設計します。",
      },
    ],
  },
  whyLearn: {
    heading: "コンポーネント設計を学ぶべき3つの理由",
    items: [
      {
        title: "フロントエンドエンジニアとしての設計力が格段に上がる",
        description:
          "「動くコード」と「良い設計のコード」の違いは、コンポーネント設計にあります。適切な粒度でコンポーネントを分割し、再利用性と保守性を両立できるスキルは、中級から上級エンジニアへのステップアップに不可欠です。",
      },
      {
        title: "チーム開発の生産性を大きく向上できる",
        description:
          "デザインシステムやStorybookを活用したコンポーネント管理は、チーム全体の開発速度と品質を向上させます。この仕組みを構築・推進できるエンジニアは、リーダーシップを発揮する機会が増えます。",
      },
      {
        title: "フロントエンドリード・デザインエンジニアへのキャリアパス",
        description:
          "コンポーネント設計のスキルは、フロントエンドチームのリード職やデザインエンジニアなど、技術とデザインの橋渡しをするポジションへのキャリアパスを開きます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでコンポーネント設計を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、上級PhaseのカリキュラムとしてReact + TypeScriptでのコンポーネント設計を学びます。基本的なReact開発スキルを身につけた上で、より高度な設計パターンと実践手法に取り組みます。",
      "実際のプロジェクトを通して、Atomic Designの適用、Storybookの導入、デザイントークンの設計、アクセシビリティ対応など、プロダクション品質のコンポーネント開発を体験します。",
    ],
    topics: [
      "Atomic Designの原則と実践",
      "コンポーネントの責務分離とProps設計",
      "Compound ComponentsとHeadless UIパターン",
      "Storybookによるコンポーネントカタログ構築",
      "デザイントークンとCSS設計（CSS Modules / Tailwind）",
      "アクセシビリティ（WAI-ARIA / キーボード操作）対応",
      "実践プロジェクト：デザインシステムの設計・構築",
    ],
  },
  careerPaths: {
    heading: "コンポーネント設計を活かせるキャリア",
    items: [
      {
        title: "フロントエンドリード",
        description:
          "フロントエンドチームの技術方針を主導し、コンポーネント設計のガイドライン策定やコードレビューを担当します。デザインシステムの構築・運用を通じて、チーム全体の開発品質を向上させます。",
        salary: "600〜1,000万円",
      },
      {
        title: "デザインエンジニア",
        description:
          "デザインと実装の両方を理解し、デザイナーとエンジニアの橋渡しをする職種です。デザインシステムの設計・実装、プロトタイピング、UIの品質向上に貢献します。",
        salary: "550〜900万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜10ヶ月（設計の良し悪しの判断基準がつかみにくい）",
        luagate: "3〜4ヶ月（実践プロジェクトで設計力を効率的に養成）",
      },
      {
        label: "挫折率",
        selfStudy: "理論は学べても実際のプロジェクトへの適用が難しい",
        luagate: "実際のUI要件に基づいた設計演習で実践力を養成",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "小規模なサンプルコンポーネントで終わりがち",
        luagate: "デザインシステム全体を設計・構築する本格プロジェクト",
      },
      {
        label: "質問対応",
        selfStudy: "コンポーネント分割の適切な粒度がわからない",
        luagate: "現役フロントエンドリードが設計レビューで指導",
      },
      {
        label: "就職サポート",
        selfStudy: "設計力のアピール方法がわからない",
        luagate: "Storybookポートフォリオで設計スキルを可視化",
      },
    ],
  },
  faq: [
    {
      question: "コンポーネント設計を学ぶ前にReactの経験はどのくらい必要ですか？",
      answer:
        "Reactの基礎（コンポーネント、State、Props、Hooks）を理解し、簡単なアプリケーションを作った経験があれば十分です。ゆめさくエンジニアではReactの基礎を習得した上でコンポーネント設計に進むカリキュラムのため、段階的にスキルアップできます。",
    },
    {
      question: "Atomic Designは実務で本当に使われていますか？",
      answer:
        "Atomic Designの考え方をそのまま採用する企業もあれば、アレンジして使う企業もあります。重要なのは「UIを適切な粒度で分割し、再利用する」という設計思考自体であり、この考え方はどのプロジェクトでも活きます。",
    },
    {
      question: "デザインの知識がなくてもコンポーネント設計を学べますか？",
      answer:
        "はい、コンポーネント設計は主にエンジニアリングの観点からUI構造を設計するスキルです。デザインセンスよりも、論理的に構造を分析・設計する能力が重要です。ただし、デザインの基礎知識があるとより深い設計ができます。",
    },
    {
      question: "Vue.jsやSvelteでもコンポーネント設計の知識は活かせますか？",
      answer:
        "はい、コンポーネント設計の原則（責務分離、Props設計、合成パターンなど）はフレームワークに依存しない普遍的な考え方です。Reactで学んだ設計力は、Vue.jsやSvelteなど他のフレームワークでもそのまま活用できます。",
    },
  ],
  relatedLanguages: [
    { slug: "react", name: "React" },
    { slug: "typescript", name: "TypeScript" },
    { slug: "ui-ux", name: "UI/UXデザイン" },
    { slug: "state-management", name: "状態管理" },
  ],
};
